import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { ScrollText } from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  FilterBar,
  DataTable,
  LoadingState,
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogs() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const fetchLogs = async (actionFilter, currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.set('action', actionFilter);
      params.set('skip', String((currentPage - 1) * LIMIT));
      params.set('limit', String(LIMIT));
      const r = await axios.get(`${API}/admin/logs?${params}`, { headers: authHeaders() });
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(filter, page); }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // DataTable render signature: (value, row) — first arg is row[col.key], second is full row
  const columns = [
    {
      key: 'created_at',
      label: t('admin.log_date'),
      sortable: true,
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">
          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      key: 'user_name',
      label: t('admin.log_user'),
      sortable: true,
      render: (_val, row) => (
        <span className="font-semibold text-xs">{row.user_name || row.user_email || '—'}</span>
      ),
    },
    {
      key: 'action',
      label: t('admin.log_action'),
      sortable: true,
      render: (_val, row) => (
        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-bold">
          {row.action || '—'}
        </span>
      ),
    },
    {
      key: 'details',
      label: t('admin.log_details'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)] max-w-xs truncate block">
          {typeof row.details === 'object'
            ? JSON.stringify(row.details)
            : row.details || '—'}
        </span>
      ),
    },
  ];

  return (
    <AdminLayout title="Activity Logs" titleTr={t('admin.log_page_title')}>
      <PageHeader
        title={t('admin.log_page_title')}
        subtitle={`${total} ${t('admin.log_page_subtitle')}`}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.log_page_title') }]}
      />

      <FilterBar
        search={filter}
        onSearchChange={setFilter}
        searchPlaceholder={t('admin.log_filter')}
        className="mb-4"
      />

      {loading ? (
        <LoadingState variant="table" rows={8} cols={4} />
      ) : (
        <DataTable
          columns={columns}
          data={logs}
          rowKey="_id"
          pageSize={LIMIT}
          emptyIcon={<ScrollText size={40} strokeWidth={1.5} />}
          emptyTitle={t('admin.log_empty_title')}
          emptyDescription={t('admin.log_empty_desc')}
          serverPagination
          currentPage={page}
          totalItems={total}
          onPageChange={(p) => { setPage(p); }}
        />
      )}
    </AdminLayout>
  );
}
